import { useEffect, useState } from "react";
import moment from "moment";
import ChevronLeftRoundedIcon from "@mui/icons-material/ChevronLeftRounded";
import ChevronRightRoundedIcon from "@mui/icons-material/ChevronRightRounded";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import ZoomInRoundedIcon from "@mui/icons-material/ZoomInRounded";
import {
  Box,
  Fade,
  IconButton,
  Modal,
  Rating,
  Typography,
} from "@mui/material";
import CustomModal from "components/customModal";
import { styles } from "./styles";
import { FeedbackOrder, FeedbackPerson, FeedbackRow } from "./types";

const FEEDBACK_TYPE_LABEL: Record<string, string> = {
  feedback: "Feedback",
  complaints: "Complaints",
  unresolved: "Unresolved",
};

const formatFeedbackType = (value: string) =>
  FEEDBACK_TYPE_LABEL[value] || value || "N/A";

const formatCreatedAtDetail = (value: string) => {
  if (!value || !moment(value).isValid()) return "N/A";
  return moment(value).format("DD/MM/YYYY, hh:mm A");
};

type FeedbackDetailsModalProps = {
  row: FeedbackRow | null;
  onClose: () => void;
  onOpenUser: (person: FeedbackPerson) => void;
  onOpenService: (person: FeedbackPerson) => void;
  onOpenOrder: (order: FeedbackOrder) => void;
};

export const FeedbackDetailsModal = ({
  row,
  onClose,
  onOpenUser,
  onOpenService,
  onOpenOrder,
}: FeedbackDetailsModalProps) => (
  <CustomModal
    title="Feedback details"
    visible={Boolean(row)}
    onClose={onClose}
    maxWidth={640}
  >
    {row ? (
      <FeedbackDetails
        row={row}
        onOpenUser={onOpenUser}
        onOpenService={onOpenService}
        onOpenOrder={onOpenOrder}
      />
    ) : null}
  </CustomModal>
);

function FeedbackDetails({
  row,
  onOpenUser,
  onOpenService,
  onOpenOrder,
}: {
  row: FeedbackRow;
  onOpenUser: (person: FeedbackPerson) => void;
  onOpenService: (person: FeedbackPerson) => void;
  onOpenOrder: (order: FeedbackOrder) => void;
}) {
  return (
    <Box>
      <Box sx={styles.detailGrid}>
        <Box>
          <Typography sx={styles.detailLabel}>Feedback type</Typography>
          <Typography sx={styles.detailValue}>
            {formatFeedbackType(row.type)}
          </Typography>
        </Box>
        <Box>
          <Typography sx={styles.detailLabel}>Rating</Typography>
          {row.rating === null ? (
            <Typography sx={styles.detailValue}>N/A</Typography>
          ) : (
            <Box sx={styles.ratingRow}>
              <Rating value={row.rating} precision={0.5} readOnly size="small" />
              <Typography sx={styles.detailValue}>{row.rating}</Typography>
            </Box>
          )}
        </Box>
        <Box>
          <Typography sx={styles.detailLabel}>Created date</Typography>
          <Typography sx={styles.detailValue}>
            {formatCreatedAtDetail(row.createdAt)}
          </Typography>
        </Box>
        <Box>
          <Typography sx={styles.detailLabel}>Order</Typography>
          {row.order.orderId && row.order.id ? (
            <div className="name-field" onClick={() => onOpenOrder(row.order)}>
              {row.order.orderId}
            </div>
          ) : (
            <Typography sx={styles.detailValue}>
              {row.order.orderId || "N/A"}
            </Typography>
          )}
        </Box>
      </Box>
      <Box sx={{ marginTop: "16px" }}>
        <Typography sx={styles.detailLabel}>Feedback</Typography>
        <Typography sx={styles.feedbackBody}>{row.feedback || "N/A"}</Typography>
        <FeedbackImageSlider images={row.feedbackImages} />
      </Box>
      <Box sx={styles.people}>
        <PersonBlock label="User" person={row.user} onOpen={onOpenUser} />
        <PersonBlock label="Merchant" person={row.merchant} onOpen={onOpenUser} />
        <PersonBlock
          label="Service partner"
          person={row.servicePartner}
          onOpen={onOpenUser}
        />
        <PersonBlock label="Service" person={row.service} onOpen={onOpenService} />
      </Box>
    </Box>
  );
}

function FeedbackImageSlider({ images }: { images: string[] }) {
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [fullscreenOpen, setFullscreenOpen] = useState(false);
  const total = images.length;
  const hasMultiple = total > 1;
  const safeIndex = ((selectedIndex % total) + total) % total;
  const activeImage = images[safeIndex];

  useEffect(() => {
    setSelectedIndex(0);
  }, [images]);

  useEffect(() => {
    if (!fullscreenOpen || !hasMultiple) return;

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "ArrowLeft") {
        setSelectedIndex((current) => (current - 1 + total) % total);
      }
      if (event.key === "ArrowRight") {
        setSelectedIndex((current) => (current + 1) % total);
      }
      if (event.key === "Escape") {
        setFullscreenOpen(false);
      }
    };

    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [fullscreenOpen, hasMultiple, total]);

  if (!total) return null;

  const goPrev = () =>
    setSelectedIndex((current) => (current - 1 + total) % total);
  const goNext = () => setSelectedIndex((current) => (current + 1) % total);

  return (
    <Box sx={styles.imageSection}>
      <Box sx={styles.galleryHeader}>
        <Typography sx={styles.galleryTitle}>Attachments</Typography>
        <Typography sx={styles.imageCount}>
          {safeIndex + 1} / {total}
        </Typography>
      </Box>

      <Box sx={styles.galleryStage}>
        <Box
          sx={styles.galleryMain}
          onClick={() => setFullscreenOpen(true)}
          role="button"
          tabIndex={0}
          onKeyDown={(event) => {
            if (event.key === "Enter" || event.key === " ") {
              event.preventDefault();
              setFullscreenOpen(true);
            }
          }}
        >
          <Box
            component="img"
            key={activeImage}
            src={activeImage}
            alt={`Feedback attachment ${safeIndex + 1}`}
            sx={styles.galleryImage}
          />
          <Box
            className="feedback-gallery-zoom-hint"
            sx={styles.galleryZoomHint}
          >
            <ZoomInRoundedIcon fontSize="small" />
            <Typography component="span">View full size</Typography>
          </Box>
        </Box>

        {hasMultiple ? (
          <>
            <IconButton
              aria-label="Previous image"
              className="feedback-gallery-prev"
              onClick={(event) => {
                event.stopPropagation();
                goPrev();
              }}
              sx={styles.galleryNavBtn}
            >
              <ChevronLeftRoundedIcon />
            </IconButton>
            <IconButton
              aria-label="Next image"
              className="feedback-gallery-next"
              onClick={(event) => {
                event.stopPropagation();
                goNext();
              }}
              sx={styles.galleryNavBtn}
            >
              <ChevronRightRoundedIcon />
            </IconButton>
          </>
        ) : null}
      </Box>

      {hasMultiple ? (
        <Box sx={styles.thumbnailRow}>
          {images.map((image, index) => {
            const isActive = index === safeIndex;
            return (
              <Box
                key={`${image}-${index}`}
                component="button"
                type="button"
                aria-label={`Show image ${index + 1}`}
                aria-current={isActive ? "true" : undefined}
                onClick={() => setSelectedIndex(index)}
                sx={{
                  ...styles.thumbnailBtn,
                  ...(isActive ? styles.thumbnailBtnActive : null),
                }}
              >
                <Box
                  component="img"
                  src={image}
                  alt=""
                  sx={styles.thumbnailImage}
                />
              </Box>
            );
          })}
        </Box>
      ) : null}

      <Modal
        open={fullscreenOpen}
        onClose={() => setFullscreenOpen(false)}
        closeAfterTransition
        aria-labelledby="feedback-image-lightbox"
      >
        <Fade in={fullscreenOpen}>
          <Box sx={styles.lightboxRoot}>
            <Box sx={styles.lightboxToolbar}>
              <Typography id="feedback-image-lightbox" sx={styles.lightboxTitle}>
                Image {safeIndex + 1} of {total}
              </Typography>
              <IconButton
                aria-label="Close image preview"
                onClick={() => setFullscreenOpen(false)}
                sx={styles.lightboxClose}
              >
                <CloseRoundedIcon />
              </IconButton>
            </Box>

            <Box sx={styles.lightboxStage}>
              <Box sx={styles.lightboxFrame}>
                <Box
                  component="img"
                  src={activeImage}
                  alt={`Feedback attachment ${safeIndex + 1}`}
                  sx={styles.lightboxImage}
                />
              </Box>

              {hasMultiple ? (
                <>
                  <IconButton
                    aria-label="Previous image"
                    className="feedback-lightbox-prev"
                    onClick={goPrev}
                    sx={styles.lightboxNavBtn}
                  >
                    <ChevronLeftRoundedIcon fontSize="large" />
                  </IconButton>
                  <IconButton
                    aria-label="Next image"
                    className="feedback-lightbox-next"
                    onClick={goNext}
                    sx={styles.lightboxNavBtn}
                  >
                    <ChevronRightRoundedIcon fontSize="large" />
                  </IconButton>
                </>
              ) : null}
            </Box>

            {hasMultiple ? (
              <Box sx={styles.lightboxThumbs}>
                {images.map((image, index) => {
                  const isActive = index === safeIndex;
                  return (
                    <Box
                      key={`lightbox-${image}-${index}`}
                      component="button"
                      type="button"
                      aria-label={`Show image ${index + 1}`}
                      onClick={() => setSelectedIndex(index)}
                      sx={{
                        ...styles.lightboxThumbBtn,
                        ...(isActive ? styles.lightboxThumbBtnActive : null),
                      }}
                    >
                      <Box
                        component="img"
                        src={image}
                        alt=""
                        sx={styles.lightboxThumbImage}
                      />
                    </Box>
                  );
                })}
              </Box>
            ) : null}
          </Box>
        </Fade>
      </Modal>
    </Box>
  );
}

function PersonBlock({
  label,
  person,
  onOpen,
}: {
  label: string;
  person: FeedbackPerson;
  onOpen: (person: FeedbackPerson) => void;
}) {
  const hasDetails = Boolean(person.name || person.phone || person.image);

  return (
    <Box>
      <Typography sx={styles.detailLabel}>{label}</Typography>
      {!hasDetails ? (
        <Typography sx={styles.detailValue}>N/A</Typography>
      ) : (
        <Box sx={styles.personRow}>
          {person.image ? (
            <Box component="img" src={person.image} alt="" sx={styles.avatar} />
          ) : null}
          <Box sx={{ minWidth: 0 }}>
            <LinkedName person={person} onOpen={onOpen} />
            {person.phone ? (
              <Typography sx={styles.plainCell}>{person.phone}</Typography>
            ) : null}
          </Box>
        </Box>
      )}
    </Box>
  );
}

export function LinkedName({
  person,
  onOpen,
}: {
  person: FeedbackPerson;
  onOpen: (person: FeedbackPerson) => void;
}) {
  if (!person?.name) {
    return <Typography sx={styles.plainCell}>N/A</Typography>;
  }
  if (!person.id) {
    return <Typography sx={styles.plainCell}>{person.name}</Typography>;
  }
  return (
    <div className="name-field" onClick={() => onOpen(person)}>
      {person.name}
    </div>
  );
}

export default FeedbackDetailsModal;
